"use client";

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/+$/, "") || "";

export default function FooterSettings() {
  const [form, setForm] = useState({
    companyName: "",
    address: "",
    email: "",
    localWhatsapp: "",
    overseasWhatsapp: "",
    facebookUrl: "",
    instagramUrl: "",
    youtubeUrl: "",
    twitterUrl: "",
    linkedinUrl: "",
    copyrightText: "", // <-- new field
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadFooter = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rockstar/admin/settings/footer`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load footer");

      if (data?.data) setForm(data.data);
    } catch (err) {
      console.error("loadFooter error:", err);
      setSnack({ open: true, message: err.message || "Failed to load data", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rockstar/admin/settings/footer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save footer");
      setSnack({ open: true, message: "Footer settings saved successfully!", severity: "success" });
    } catch (err) {
      console.error("saveFooter error:", err);
      setSnack({ open: true, message: err.message || "Failed to save", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadFooter();
  }, [loadFooter]);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: 6,
        pb: 8,
        px: 2,
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 850,
          backgroundColor: "rgba(18,18,18,0.9)",
          borderRadius: 2,
          p: { xs: 3, md: 4 },
          boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
          color: "#fff",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Footer Settings
          </Typography>
          <Box>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={loadFooter}
              disabled={loading || saving}
              sx={{
                mr: 1,
                borderColor: "#c9a227",
                color: "#c9a227",
                "&:hover": { borderColor: "#facc15", color: "#facc15" },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
              sx={{
                backgroundColor: "#c9a227",
                "&:hover": { backgroundColor: "#facc15" },
                color: "#000",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Company Name & Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  multiline
                  rows={2}
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>

              {/* WhatsApp Numbers */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Local WhatsApp"
                  name="localWhatsapp"
                  value={form.localWhatsapp}
                  onChange={handleChange}
                  placeholder="077 232 2467"
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Overseas WhatsApp"
                  name="overseasWhatsapp"
                  value={form.overseasWhatsapp}
                  onChange={handleChange}
                  placeholder="+94 77 232 2467"
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>

              {/* Social Links */}
              {[
                ["facebookUrl", "Facebook URL"],
                ["instagramUrl", "Instagram URL"],
                ["youtubeUrl", "YouTube URL"],
                ["twitterUrl", "Twitter URL"],
                ["linkedinUrl", "LinkedIn URL"],
              ].map(([name, label]) => (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    fullWidth
                    label={label}
                    name={name}
                    value={form[name] || ""}
                    onChange={handleChange}
                    InputLabelProps={{ style: { color: "#9ca3af" } }}
                    InputProps={{ style: { color: "#fff" } }}
                  />
                </Grid>
              ))}

              {/* Copyright Text - NEW */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Copyright Text"
                  name="copyrightText"
                  value={form.copyrightText || ""}
                  onChange={handleChange}
                  placeholder="© 2025 Rockstar Bot Trading | Developed By Vibrantick Infotech Solutions"
                  InputLabelProps={{ style: { color: "#9ca3af" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#9ca3af" }}>
                  You can include HTML like &lt;strong&gt; or &lt;a href=&quot;#&quot;&gt;link&lt;/a&gt; — you'll
                  need to render HTML on the public site if used.
                </Typography>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
